import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import FeatherIcon from 'feather-icons-react';

import { SuiteInspectionListTable } from './Components/SuiteInspectionListTable';
import { PropertySelector } from './Components/PropertySelector';
import { DeleteSuiteInspectionConfirmationModal } from './Components/DeleteSuiteInspectionConfirmationModal';

import { getPropertyList } from '../../../Inventory/pages/FacilityInventory/Helpers/Api';

import {
  createSuiteInspection,
  getSuiteInspectionList,
  updateSuiteInspection,
  deleteSuiteInspection,
  exportSuiteInspectionsToExcel,
  getMasterSheetUnits,
} from './Components/FormikHelpers/Api';

import { setLoading } from '../../../../store/actions';
import './Components/suite-inspection.scss';

const GenerateSuiteInspection = () => {
  const dispatch = useDispatch();

  const [properties, setProperties] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [masterSheetUnits, setMasterSheetUnits] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [buildingType, setBuildingType] = useState('Apartment');

  const [filters, setFilters] = useState(() => {
    // The Resident record panel links here with the unit already chosen.
    const q = new URLSearchParams(window.location.search);
    return {
    property_id: q.get('property_id') || '',
    building_name: q.get('building_name') || '',
    startDate: '',
    endDate: '',
    type: '',
    overall_status: '',
    unit_number: q.get('unit_number') || '',
    };
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch { return null; }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    setUser(currentUser);
    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      Swal.fire('Error', 'Failed to load properties', 'error');
    } finally { dispatch(setLoading(false)); }
  }, [dispatch, getUserFromToken]);

  const fetchInspections = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      const response = await getSuiteInspectionList({
        propertyId: filters.property_id || undefined,
        building_name: filters.building_name || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        type: filters.type || undefined,
        overall_status: filters.overall_status || undefined,
        unit_number: filters.unit_number || undefined,
      });
      setInspections(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch suite inspections:', error);
      setInspections([]);
      Swal.fire('Error', 'Failed to load suite inspection checklists', 'error');
    } finally { dispatch(setLoading(false)); }
  }, [dispatch, filters]);

  const fetchMasterSheetUnits = useCallback(async () => {
    if (!filters.property_id || !filters.building_name) { setMasterSheetUnits([]); return; }
    try {
      const result = await getMasterSheetUnits(filters.property_id, filters.building_name);
      setMasterSheetUnits(result || []);
    } catch (err) { console.error('Failed to fetch master sheet units:', err); setMasterSheetUnits([]); }
  }, [filters.property_id, filters.building_name]);

  const handleSaveRow = useCallback(async (rowData) => {
    try {
      dispatch(setLoading(true));
      const { _pendingFiles, ...rest } = rowData;
      const payload = {
        ...rest,
        property_details: { property_id: filters.property_id, building_name: filters.building_name },
        type: buildingType.toLowerCase(),
      };
      let response;
      if (rowData._id) {
        response = await updateSuiteInspection(rowData._id, payload);
      } else {
        response = await createSuiteInspection(payload);
      }
      await fetchInspections();
      return { success: true, data: response?.data || response };
    } catch (error) {
      console.error('Failed to save suite inspection:', error);
      throw error;
    } finally { dispatch(setLoading(false)); }
  }, [dispatch, filters, buildingType, fetchInspections]);

  const handleDeleteConfirm = async () => {
    if (!selectedInspection?._id) return;
    try {
      dispatch(setLoading(true));
      await deleteSuiteInspection(selectedInspection._id);
      Swal.fire('Deleted', 'Suite Inspection Checklist deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setSelectedInspection(null);
      fetchInspections();
    } catch (err) { Swal.fire('Error', 'Failed to delete inspection', 'error'); }
    finally { dispatch(setLoading(false)); }
  };

  const handleExportExcel = async () => {
    if (!filters.property_id) { toast.warn('Please select a property first'); return; }
    try {
      await exportSuiteInspectionsToExcel({
        propertyId: filters.property_id,
        building_name: filters.building_name || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        type: buildingType ? buildingType.toLowerCase() : undefined,
        overall_status: filters.overall_status || undefined,
        unit_number: filters.unit_number || undefined,
      });
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  useEffect(() => { fetchProperties(); }, [fetchProperties]);
  useEffect(() => { fetchInspections(); }, [fetchInspections]);
  useEffect(() => { fetchMasterSheetUnits(); }, [fetchMasterSheetUnits]);

  return (
    <div className="page-content">
      <MetaTags><title>Rentdigicare | Suite Inspection Checklists</title></MetaTags>
      <div className="container-fluid mb-2">
        <Breadcrumbs title="Maintenance" breadcrumbItem="Suite Inspection Checklists" />

        <PropertySelector
          propertyList={properties}
          selectedFilters={filters}
          onPropertySelectionChange={setFilters}
          onExportExcel={handleExportExcel}
          onBuildingTypeChange={setBuildingType}
          user={user}
        />

        <SuiteInspectionListTable
          items={inspections}
          masterSheetUnits={masterSheetUnits}
          buildingType={buildingType}
          filters={filters}
          onDelete={(inspection) => { setSelectedInspection(inspection); setIsDeleteModalOpen(true); }}
          onSaveRow={handleSaveRow}
          onRefresh={fetchInspections}
          userRole={user?.role || 'other'}
        />

        <DeleteSuiteInspectionConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={handleDeleteConfirm}
          inspection={selectedInspection}
        />
      </div>
    </div>
  );
};

export { GenerateSuiteInspection };